import React, { useEffect, useMemo, useState, useRef } from "react";

// 🎨 LIGHT / DARK TEMA ALGILAYICI HOOK
export function useDetectTheme() {
  const [theme, setTheme] = useState(() => {
    if (typeof document === "undefined") return "dark";
    if (document.documentElement.classList.contains("dark")) return "dark";
    if (document.body.classList.contains("wm-admin-light") || document.querySelector(".wm-admin-light")) return "light";
    const saved = localStorage.getItem("wm_publisher_theme") || localStorage.getItem("wolf-admin-theme");
    return saved === "light" ? "light" : "dark";
  });

  useEffect(() => {
    const evaluate = () => {
      const isLight = document.body.classList.contains("wm-admin-light") ||
                      document.querySelector(".wm-admin-light") !== null ||
                      (!document.documentElement.classList.contains("dark") && (
                        localStorage.getItem("wm_publisher_theme") === "light" ||
                        localStorage.getItem("wolf-admin-theme") === "light"
                      ));
      setTheme(isLight ? "light" : "dark");
    };

    evaluate();
    const observer = new MutationObserver(evaluate);
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ["class"] });
    observer.observe(document.body, { attributes: true, attributeFilter: ["class"] });
    window.addEventListener("storage", evaluate);
    return () => {
      observer.disconnect();
      window.removeEventListener("storage", evaluate);
    };
  }, []);

  return theme;
}

const IS_LOCAL = typeof window !== "undefined" && ["localhost", "127.0.0.1"].includes(window.location.hostname);
const API_BASE = IS_LOCAL ? "https://www.wolfmediaajans.com" : "";
const API_PERFORMANCE_SUMMARY = `${API_BASE}/api/performance-summary.php`;

// 🌟 YARDIMCI FORMAT VE TARİH FONKSİYONLARI
export function formatMetricNumber(val) {
  const num = Number(val) || 0;
  if (num === 0) return "0";
  if (num >= 1000000) {
    const m = Math.floor(num / 1000000);
    const rem = num % 1000000;
    const k = Math.round(rem / 1000);
    return k > 0 ? `${m}M ${k}K` : `${m}M`;
  }
  if (num >= 1000) {
    const kVal = num / 1000;
    if (Number.isInteger(kVal)) {
      return `${kVal}K`;
    }
    const formatted = kVal.toLocaleString("tr-TR", { minimumFractionDigits: 1, maximumFractionDigits: 2 });
    return `${formatted}K`;
  }
  return num.toLocaleString("tr-TR");
}

export function formatLiveDuration(seconds) {
  const sec = Math.max(0, Number(seconds) || 0);
  const h = Math.floor(sec / 3600);
  const m = Math.floor((sec % 3600) / 60);
  const s = Math.floor(sec % 60);

  if (h > 0) {
    return `${h}s ${m}dk ${s}sn`;
  }
  if (m > 0) {
    return `${m}dk ${s}sn`;
  }
  return `${s}sn`;
}

function pad2(n) {
  return String(n).padStart(2, "0");
}

export function formatDateTr(dateObj) {
  if (!dateObj || isNaN(dateObj.getTime())) return "";
  return `${pad2(dateObj.getDate())}.${pad2(dateObj.getMonth() + 1)}.${dateObj.getFullYear()}`;
}

export function formatDateIso(dateObj) {
  if (!dateObj || isNaN(dateObj.getTime())) return "";
  return `${dateObj.getFullYear()}-${pad2(dateObj.getMonth() + 1)}-${pad2(dateObj.getDate())}`;
}

export function parseDateStr(str) {
  if (!str) return new Date();
  if (str instanceof Date) return str;
  if (typeof str === "string") {
    let clean = str.trim();
    if (clean.includes(" ")) clean = clean.split(" ")[0];
    if (clean.includes("T")) clean = clean.split("T")[0];
    if (clean.includes("~")) clean = clean.split("~")[0].trim();

    if (clean.includes("-")) {
      const parts = clean.split("-");
      if (parts.length === 3) {
        return new Date(Number(parts[0]), Number(parts[1]) - 1, Number(parts[2]));
      }
    }
    if (clean.includes(".")) {
      const parts = clean.split(".");
      if (parts.length === 3) {
        return new Date(Number(parts[2]), Number(parts[1]) - 1, Number(parts[0]));
      }
      if (parts.length === 2) {
        const nowYear = new Date().getFullYear();
        return new Date(nowYear, Number(parts[1]) - 1, Number(parts[0]));
      }
    }
  }
  const d = new Date(str);
  return isNaN(d.getTime()) ? new Date() : d;
}

export const TURKISH_MONTH_NAMES = [
  "Ocak", "Şubat", "Mart", "Nisan", "Mayıs", "Haziran",
  "Temmuz", "Ağustos", "Eylül", "Ekim", "Kasım", "Aralık"
];

export const COUNTRY_NAMES = {
  TR: { name: "Türkiye", flag: "🇹🇷" },
  DE: { name: "Almanya", flag: "🇩🇪" },
  AZ: { name: "Azerbaycan", flag: "🇦🇿" },
  US: { name: "Amerika Birleşik Devletleri", flag: "🇺🇸" },
  GB: { name: "Birleşik Krallık", flag: "🇬🇧" },
  NL: { name: "Hollanda", flag: "🇳🇱" },
  FR: { name: "Fransa", flag: "🇫🇷" },
  BE: { name: "Belçika", flag: "🇧🇪" },
  AT: { name: "Avusturya", flag: "🇦🇹" },
  CH: { name: "İsviçre", flag: "🇨🇭" },
  SE: { name: "İsveç", flag: "🇸🇪" },
  NO: { name: "Norveç", flag: "🇳🇴" },
  DK: { name: "Danimarka", flag: "🇩🇰" },
  RU: { name: "Rusya", flag: "🇷🇺" },
  KZ: { name: "Kazakistan", flag: "🇰🇿" },
  UZ: { name: "Özbekistan", flag: "🇺🇿" },
  SA: { name: "Suudi Arabistan", flag: "🇸🇦" },
  AE: { name: "Birleşik Arap Emirlikleri", flag: "🇦🇪" },
  IQ: { name: "Irak", flag: "🇮🇶" },
  SY: { name: "Suriye", flag: "🇸🇾" },
  CY: { name: "Kıbrıs", flag: "🇨🇾" },
  IT: { name: "İtalya", flag: "🇮🇹" },
  ES: { name: "İspanya", flag: "🇪🇸" },
  CA: { name: "Kanada", flag: "🇨🇦" },
  AU: { name: "Avustralya", flag: "🇦🇺" }
};

// 📦 GERÇEK VERİ HARİTALAMA MOTORU (GELECEK VE BOŞ GÜNLER KESİNLİKLE 0 OLUR)
export function getDailyDataForRange(startDate, endDate, providedDaily = []) {
  const map = new Map();
  const today = new Date();
  today.setHours(23, 59, 59, 999);

  // 1. Gerçek API verilerini ISO anahtarıyla haritalandır
  if (Array.isArray(providedDaily) && providedDaily.length > 0) {
    providedDaily.forEach(d => {
      const dObj = parseDateStr(d.report_date || d.date || d.dateIso || d.dateTr);
      const iso = formatDateIso(dObj);
      const dia = Number(d.diamonds || d.totalDiamonds || 0);
      const sec = Number(d.liveSeconds || d.live_seconds || d.totalLiveSeconds || 0);
      const count = Number(d.liveCount || d.live_count || d.totalLiveCount || 0);
      const vDays = Number(d.validDays || d.valid_days || (sec >= 3600 ? 1 : 0));
      const followers = Number(d.newFollowers || d.new_followers || 0);

      const matches = Number(d.matches || 0);
      const matchDia = Number(d.match_diamonds || d.matchDiamonds || 0);
      const guestDia = Number(d.multi_guest_diamonds || d.multiGuestDiamonds || 0);
      const hostDia = Number(d.multi_host_diamonds || d.multiHostDiamonds || 0);
      const guestOnlyDia = Number(d.multi_guest_only_diamonds || d.multiGuestOnlyDiamonds || 0);

      const foreignDia = Number(d.foreign_diamonds || d.foreignDiamonds || 0);
      const foreignRate = Number(d.foreign_diamond_rate || d.foreignDiamondRate || 0);
      const fanClubDia = Number(d.fan_club_diamonds || d.fanClubDiamonds || 0);
      const newFans = Number(d.new_fans || d.newFans || 0);

      map.set(iso, {
        dateIso: iso,
        dateTr: formatDateTr(dObj),
        diamonds: dia,
        liveSeconds: sec,
        liveCount: count,
        validDays: vDays,
        newFollowers: followers,
        matches: matches,
        match_diamonds: matchDia,
        multi_guest_diamonds: guestDia,
        multi_host_diamonds: hostDia,
        multi_guest_only_diamonds: guestOnlyDia,
        foreign_diamonds: foreignDia,
        foreign_diamond_rate: foreignRate,
        foreign_regions: d.foreign_regions || "",
        fan_club_diamonds: fanClubDia,
        new_fans: newFans
      });
    });
  }

  const result = [];
  const curr = new Date(startDate.getTime());
  const end = new Date(endDate.getTime());
  curr.setHours(0, 0, 0, 0);
  end.setHours(23, 59, 59, 999);

  while (curr <= end) {
    const iso = formatDateIso(curr);

    if (map.has(iso)) {
      result.push(map.get(iso));
    } else {
      result.push({
        dateIso: iso,
        dateTr: formatDateTr(curr),
        diamonds: 0,
        liveSeconds: 0,
        liveCount: 0,
        validDays: 0,
        newFollowers: 0,
        matches: 0,
        match_diamonds: 0,
        multi_guest_diamonds: 0,
        multi_host_diamonds: 0,
        multi_guest_only_diamonds: 0,
        foreign_diamonds: 0,
        foreign_diamond_rate: 0,
        foreign_regions: "",
        fan_club_diamonds: 0,
        new_fans: 0
      });
    }
    curr.setDate(curr.getDate() + 1);
  }

  return result;
}

// 🧮 DÖNEM BAZLI ÖZET HESAPLAMA MOTORU
function calculatePeriodSummary(dayList) {
  if (!dayList || dayList.length === 0) {
    return {
      diamonds: 0,
      liveDays: 0,
      validDays: 0,
      liveSeconds: 0,
      liveCount: 0,
      newFollowers: 0,
      matches: 0,
      matchDiamonds: 0,
      multiGuestDiamonds: 0,
      multiHostDiamonds: 0,
      multiGuestOnlyDiamonds: 0,
      foreignDiamonds: 0,
      fanClubDiamonds: 0,
      newFans: 0
    };
  }

  const totalDiamonds = dayList.reduce((s, d) => s + (Number(d.diamonds) || 0), 0);
  const liveDays = dayList.filter(d => (Number(d.liveSeconds) >= 3600 || Number(d.validDays) > 0)).length;
  const validDays = dayList.reduce((s, d) => s + (Number(d.validDays) || 0), 0);
  const totalLiveSeconds = dayList.reduce((s, d) => s + (Number(d.liveSeconds) || 0), 0);
  const totalLiveCount = dayList.reduce((s, d) => s + (Number(d.liveCount) || 0), 0);
  const totalFollowers = dayList.reduce((s, d) => s + (Number(d.newFollowers) || 0), 0);

  const totalMatches = dayList.reduce((s, d) => s + (Number(d.matches) || 0), 0);
  const totalMatchDiamonds = dayList.reduce((s, d) => s + (Number(d.match_diamonds) || 0), 0);
  const totalMultiGuestDiamonds = dayList.reduce((s, d) => s + (Number(d.multi_guest_diamonds) || 0), 0);
  const totalMultiHostDiamonds = dayList.reduce((s, d) => s + (Number(d.multi_host_diamonds) || 0), 0);
  const totalMultiGuestOnlyDiamonds = dayList.reduce((s, d) => s + (Number(d.multi_guest_only_diamonds) || 0), 0);
  const totalForeignDiamonds = dayList.reduce((s, d) => s + (Number(d.foreign_diamonds) || 0), 0);
  const totalFanClubDiamonds = dayList.reduce((s, d) => s + (Number(d.fan_club_diamonds) || 0), 0);
  const totalNewFans = dayList.reduce((s, d) => s + (Number(d.new_fans) || 0), 0);

  return {
    diamonds: totalDiamonds,
    liveDays: liveDays,
    validDays: validDays,
    liveSeconds: totalLiveSeconds,
    liveCount: totalLiveCount,
    newFollowers: totalFollowers,
    matches: totalMatches,
    matchDiamonds: totalMatchDiamonds,
    multiGuestDiamonds: totalMultiGuestDiamonds,
    multiHostDiamonds: totalMultiHostDiamonds,
    multiGuestOnlyDiamonds: totalMultiGuestOnlyDiamonds,
    foreignDiamonds: totalForeignDiamonds,
    fanClubDiamonds: totalFanClubDiamonds,
    newFans: totalNewFans
  };
}

// 👑 1. TEMEL METRİK KARTLARI
export function BackstageKpiCards({
  summary = {},
  previousSummary = {},
  dateRangeLabel = "",
  previousDateRangeLabel = "",
  activePeriod = "month",
  onPeriodChange = () => {},
  selectedYear = new Date().getFullYear(),
  selectedMonth = new Date().getMonth(),
  onMonthSelect = () => {},
  customStartDate = "",
  customEndDate = "",
  onCustomStartChange = () => {},
  onCustomEndChange = () => {},
  onQuickPreset = () => {}
}) {
  const cards = useMemo(() => {
    const diamonds = Number(summary.diamonds || 0);
    const prevDiamonds = Number(previousSummary.diamonds || 0);
    const diamondDiff = prevDiamonds > 0 ? ((diamonds - prevDiamonds) / prevDiamonds) * 100 : (diamonds > 0 ? 100 : 0);

    const liveSec = Number(summary.liveSeconds || 0);
    const prevLiveSec = Number(previousSummary.liveSeconds || 0);
    const liveSecDiff = prevLiveSec > 0 ? ((liveSec - prevLiveSec) / prevLiveSec) * 100 : (liveSec > 0 ? 100 : 0);

    const validDays = Number(summary.validDays ?? summary.liveDays ?? 0);
    const prevValidDays = Number(previousSummary.validDays ?? previousSummary.liveDays ?? 0);
    const validDaysDiff = prevValidDays > 0 ? ((validDays - prevValidDays) / prevValidDays) * 100 : (validDays > 0 ? 100 : 0);

    const matches = Number(summary.matches || 0);
    const matchDia = Number(summary.matchDiamonds || 0);
    const prevMatchDia = Number(previousSummary.matchDiamonds || 0);
    const matchDiaDiff = prevMatchDia > 0 ? ((matchDia - prevMatchDia) / prevMatchDia) * 100 : (matchDia > 0 ? 100 : 0);

    const foreignDia = Number(summary.foreignDiamonds || 0);
    const prevForeignDia = Number(previousSummary.foreignDiamonds || 0);
    const foreignDiaDiff = prevForeignDia > 0 ? ((foreignDia - prevForeignDia) / prevForeignDia) * 100 : (foreignDia > 0 ? 100 : 0);
    const foreignRate = diamonds > 0 ? ((foreignDia / diamonds) * 100).toFixed(1) : "0";

    const fanClubDia = Number(summary.fanClubDiamonds || 0);
    const prevFanClubDia = Number(previousSummary.fanClubDiamonds || 0);
    const fanClubDiff = prevFanClubDia > 0 ? ((fanClubDia - prevFanClubDia) / prevFanClubDia) * 100 : (fanClubDia > 0 ? 100 : 0);
    const activeFans = Number(summary.activeFans || 0);

    return [
      {
        id: "diamonds",
        title: "💎 Toplam Elmas",
        tooltip: "Seçili dönemde kazanılan toplam net elmas miktarı",
        value: formatMetricNumber(diamonds),
        rawPrev: formatMetricNumber(prevDiamonds),
        diff: diamondDiff,
        isPositive: diamondDiff >= 0,
        subBadge: diamonds > 0 ? `${validDays} Geçerli Gün` : null
      },
      {
        id: "live_duration",
        title: "⏱️ CANLI Yayın Süresi",
        tooltip: "Toplam canlı yayında kalınan süre ve 1 saat üzeri geçerli yayın günleri",
        value: formatLiveDuration(liveSec),
        rawPrev: formatLiveDuration(prevLiveSec),
        diff: liveSecDiff,
        isPositive: liveSecDiff >= 0,
        subBadge: `${validDays} Gün Geçerli`
      },
      {
        id: "valid_days",
        title: "📅 Geçerli Yayın Günü",
        tooltip: "TikTok Backstage tarafından hesaplanan, en az 1 saat yayın yapılan geçerli yayın günleri sayısı",
        value: `${validDays} Gün`,
        rawPrev: `${prevValidDays} Gün`,
        diff: validDaysDiff,
        isPositive: validDaysDiff >= 0,
        subBadge: "1 Saat Üzeri Yayınlar"
      },
      {
        id: "foreign_diamonds",
        title: "🌍 Yurt Dışı Geliri",
        tooltip: "Türkiye dışındaki izleyicilerden kazanılan elmas ve toplam gelire oranı",
        value: formatMetricNumber(foreignDia),
        rawPrev: formatMetricNumber(prevForeignDia),
        diff: foreignDiaDiff,
        isPositive: foreignDiaDiff >= 0,
        subBadge: `%${foreignRate} Global Pay`
      },
      {
        id: "match_diamonds",
        title: "⚔️ PK & Maç Kazancı",
        tooltip: "Eşleştirmelerden ve PK savaşlarından kazanılan elmas miktarı",
        value: formatMetricNumber(matchDia),
        rawPrev: formatMetricNumber(prevMatchDia),
        diff: matchDiaDiff,
        isPositive: matchDiaDiff >= 0,
        subBadge: matches > 0 ? `${matches} PK Maçı` : null
      },
      {
        id: "fan_club",
        title: "⭐ Hayran Kulübü",
        tooltip: "Hayran kulübünden toplanan toplam elmas ve aktif üye sayısı",
        value: formatMetricNumber(fanClubDia),
        rawPrev: formatMetricNumber(prevFanClubDia),
        diff: fanClubDiff,
        isPositive: fanClubDiff >= 0,
        subBadge: activeFans > 0 ? `${activeFans} Aktif Üye` : null
      }
    ];
  }, [summary, previousSummary]);

  const monthOptions = useMemo(() => {
    const list = [];
    const now = new Date();
    const currYear = now.getFullYear();
    const currMonth = now.getMonth();

    for (let offset = 0; offset >= -24; offset--) {
      const d = new Date(currYear, currMonth + offset, 1);
      const y = d.getFullYear();
      const m = d.getMonth();
      const isCurrent = offset === 0;
      const isPrevious = offset === -1;
      const label = `${TURKISH_MONTH_NAMES[m]} ${y}` + (isCurrent ? " (Bu Ay)" : isPrevious ? " (Geçen Ay)" : "");
      list.push({ year: y, month: m, label, key: `${y}-${m}` });
    }
    return list;
  }, []);

  return (
    <div className="rounded-3xl border border-white/10 bg-zinc-950/80 backdrop-blur-xl p-5 sm:p-6 shadow-2xl">
      <div className="flex flex-wrap items-center justify-between gap-4 border-b border-white/10 pb-4 mb-4">
        <div>
          <h3 className="text-base sm:text-lg font-black text-white flex items-center gap-2">
            <span>⚡ Temel Metrikler</span>
            <span className="text-xs font-normal text-zinc-400 font-mono">UTC+00:00</span>
          </h3>
          <p className="text-xs text-zinc-400 mt-0.5">
            Seçilen dönemin önceki dönemle kıyaslamalı canlı yayın performansı
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <div className="flex items-center gap-1 rounded-2xl bg-zinc-900/90 p-1 border border-white/10 text-xs shadow-inner">
            <button
              type="button"
              onClick={() => onPeriodChange("month")}
              className={`rounded-xl px-3.5 py-1.5 font-bold transition-all cursor-pointer ${
                activePeriod === "month"
                  ? "bg-amber-500 text-black shadow-lg shadow-amber-500/20 scale-105"
                  : "text-zinc-400 hover:text-white hover:bg-white/5"
              }`}
            >
              Bu ay
            </button>

            <button
              type="button"
              onClick={() => onPeriodChange("week")}
              className={`rounded-xl px-3.5 py-1.5 font-bold transition-all cursor-pointer ${
                activePeriod === "week"
                  ? "bg-amber-500 text-black shadow-lg shadow-amber-500/20 scale-105"
                  : "text-zinc-400 hover:text-white hover:bg-white/5"
              }`}
            >
              Bu hafta
            </button>

            <button
              type="button"
              onClick={() => onPeriodChange("yesterday")}
              className={`rounded-xl px-3.5 py-1.5 font-bold transition-all cursor-pointer ${
                activePeriod === "yesterday"
                  ? "bg-amber-500 text-black shadow-lg shadow-amber-500/20 scale-105"
                  : "text-zinc-400 hover:text-white hover:bg-white/5"
              }`}
            >
              Dün
            </button>

            <button
              type="button"
              onClick={() => onPeriodChange("custom")}
              className={`rounded-xl px-3.5 py-1.5 font-bold transition-all cursor-pointer ${
                activePeriod === "custom"
                  ? "bg-amber-500 text-black shadow-lg shadow-amber-500/20 scale-105"
                  : "text-zinc-400 hover:text-white hover:bg-white/5"
              }`}
            >
              Özel
            </button>
          </div>

          <div className="relative flex items-center">
            <select
              value={`${selectedYear}-${selectedMonth}`}
              onChange={(e) => {
                const [y, m] = e.target.value.split("-").map(Number);
                onMonthSelect(y, m);
              }}
              className="appearance-none rounded-2xl border border-amber-500/40 bg-zinc-900/95 py-1.5 pl-3 pr-8 text-xs font-bold text-amber-300 focus:border-amber-400 focus:outline-none cursor-pointer shadow-md"
            >
              {monthOptions.map((opt) => (
                <option key={opt.key} value={opt.key} className="bg-zinc-900 text-white font-medium">
                  {opt.label}
                </option>
              ))}
            </select>
            <span className="pointer-events-none absolute right-2.5 text-xs text-amber-400">▾</span>
          </div>

          <div className="hidden lg:flex items-center gap-1.5 rounded-2xl border border-white/10 bg-white/[0.03] px-3 py-1.5 text-xs text-zinc-300 font-mono">
            <span>📅</span>
            <span className="font-semibold text-amber-300">{dateRangeLabel}</span>
          </div>
        </div>
      </div>

      {activePeriod === "custom" && (
        <div className="mb-5 rounded-2xl border border-amber-500/30 bg-gradient-to-r from-amber-500/10 via-zinc-900 to-black p-4 text-xs transition-all animate-fadeIn">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div className="flex flex-wrap items-center gap-3">
              <span className="font-bold text-amber-400 flex items-center gap-1.5">
                <span>🗓️</span> Özel Tarih Aralığı:
              </span>
              
              <div className="flex items-center gap-2">
                <input
                  type="date"
                  value={customStartDate}
                  onChange={(e) => onCustomStartChange(e.target.value)}
                  className="rounded-xl border border-white/20 bg-zinc-900 px-3 py-1.5 text-xs font-mono text-white focus:border-amber-500 focus:outline-none"
                />
                <span className="text-zinc-500 font-bold">-</span>
                <input
                  type="date"
                  value={customEndDate}
                  onChange={(e) => onCustomEndChange(e.target.value)}
                  className="rounded-xl border border-white/20 bg-zinc-900 px-3 py-1.5 text-xs font-mono text-white focus:border-amber-500 focus:outline-none"
                />
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-1.5">
              <span className="text-zinc-400 text-[11px] font-medium mr-1">Hızlı Seç:</span>
              {[
                { label: "Son 7 Gün", days: 7 },
                { label: "Son 14 Gün", days: 14 },
                { label: "Son 30 Gün", days: 30 },
                { label: "Son 60 Gün", days: 60 }
              ].map((chip, cIdx) => (
                <button
                  key={cIdx}
                  type="button"
                  onClick={() => onQuickPreset(chip)}
                  className="rounded-lg border border-white/10 bg-white/5 px-2.5 py-1 text-[11px] font-medium text-zinc-300 hover:border-amber-500/50 hover:bg-amber-500/10 hover:text-amber-300 transition cursor-pointer"
                >
                  {chip.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3.5 sm:gap-4">
        {cards.map((card) => (
          <div
            key={card.id}
            className="group relative overflow-hidden rounded-2xl border border-white/10 bg-gradient-to-br from-white/[0.04] to-white/[0.01] p-4 transition-all hover:border-amber-500/40 hover:bg-white/[0.06] shadow-md"
          >
            <div className="flex items-center justify-between text-xs text-zinc-400 mb-1.5">
              <span className="font-semibold text-zinc-300">{card.title}</span>
              <span className="cursor-help text-zinc-500 hover:text-zinc-300" title={card.tooltip}>
                ⓘ
              </span>
            </div>

            <div className="text-2xl sm:text-3xl font-black tracking-tight text-white font-mono my-1">
              {card.value}
            </div>

            <div className="flex items-center gap-2 text-[11px] text-zinc-400 mt-2 flex-wrap">
              <span className="text-[10px] text-zinc-400" title={previousDateRangeLabel}>
                {previousDateRangeLabel} tarihine göre:
              </span>
              <span className="font-mono text-zinc-400">{card.rawPrev}</span>
              <span
                className={`inline-flex items-center gap-0.5 font-bold font-mono px-1.5 py-0.5 rounded text-[10px] ${
                  card.isPositive
                    ? "bg-emerald-500/15 text-emerald-400 border border-emerald-500/30"
                    : "bg-rose-500/15 text-rose-400 border border-rose-500/30"
                }`}
              >
                {card.isPositive ? "▲" : "▼"} %{Math.abs(card.diff).toFixed(2).replace(".", ",")}
              </span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// 💰 2. GELİR KANALLARI KIRILIMI + GLOBAL KİTLE VE HAYRAN KULÜBÜ ANALİZİ
export function BackstageRevenueAndRegions({
  summary = {},
  previousSummary = {},
  revenueBreakdown = null,
  topRegions = []
}) {
  const theme = useDetectTheme();
  const isLight = theme === "light";

  const totalDiamonds = Number(summary.diamonds || summary.totalDiamonds || 0);
  const matchDiamonds = Number(summary.matchDiamonds || 0);
  const foreignDiamonds = Number(summary.foreignDiamonds || 0);
  const guestDiamonds = Number(summary.multiGuestDiamonds || 0);
  const fanClubDiamonds = Number(summary.fanClubDiamonds || 0);

  // Kendi solo yayınlarından gelen standart elmas payı
  const soloDiamonds = Math.max(0, totalDiamonds - (matchDiamonds + guestDiamonds));

  const channels = useMemo(() => {
    if (Array.isArray(revenueBreakdown) && revenueBreakdown.length > 0) {
      return revenueBreakdown;
    }

    const calcPctVal = (val) => totalDiamonds > 0 ? ((val / totalDiamonds) * 100).toFixed(1) : "0.0";

    return [
      {
        id: "pk",
        label: "⚔️ PK & Eşleştirme Maçları",
        tooltip: "Canlı yayın savaşlarından ve PK eşleşmelerinden kazanılan elmaslar",
        diamonds: matchDiamonds,
        pct: Number(calcPctVal(matchDiamonds)),
        color: "#eab308",
        badge: `${summary.matches || 0} Maç`
      },
      {
        id: "foreign",
        label: "🌍 Yurt Dışı İzleyicileri",
        tooltip: "Türkiye dışındaki yabancı ülke izleyicilerinden gelen elmaslar",
        diamonds: foreignDiamonds,
        pct: Number(calcPctVal(foreignDiamonds)),
        color: "#06b6d4",
        badge: "Global Kitle"
      },
      {
        id: "guest",
        label: "👥 Çoklu Konuk Yayınları",
        tooltip: "Çoklu konuk modunda yayıncı veya konuk olarak kazanılan elmaslar",
        diamonds: guestDiamonds,
        pct: Number(calcPctVal(guestDiamonds)),
        color: "#10b981",
        badge: "Konuk Odaları"
      },
      {
        id: "fan_club",
        label: "⭐ Hayran Kulübü Katkısı",
        tooltip: "Hayran kulübü üyelerinden toplanan özel elmaslar",
        diamonds: fanClubDiamonds,
        pct: Number(calcPctVal(fanClubDiamonds)),
        color: "#ec4899",
        badge: `${summary.activeFans || 0} Aktif Üye`
      }
    ];
  }, [revenueBreakdown, totalDiamonds, matchDiamonds, foreignDiamonds, guestDiamonds, fanClubDiamonds, summary]);

  // Donut SVG Segmentleri
  const circumference = 2 * Math.PI * 38; // ~238.761
  const donutSlices = useMemo(() => {
    let cum = 0;
    return channels.map((c) => {
      const slicePct = Math.min(100, Math.max(0, Number(c.pct) || 0));
      const sliceLen = (slicePct / 100) * circumference;
      const offset = -(cum / 100) * circumference;
      cum += slicePct;
      return {
        ...c,
        strokeDasharray: `${sliceLen.toFixed(1)} ${(circumference - sliceLen).toFixed(1)}`,
        strokeDashoffset: offset.toFixed(1)
      };
    });
  }, [channels, circumference]);

  const dominantChannel = useMemo(() => {
    return channels.reduce((prev, curr) => (curr.pct > prev.pct ? curr : prev), channels[0]);
  }, [channels]);

  // Bölge listesi
  const regionsList = useMemo(() => {
    if (Array.isArray(topRegions) && topRegions.length > 0) {
      return topRegions;
    }
    const raw = summary.foreign_regions || summary.foreignRegions || "";
    if (typeof raw === "string" && raw.startsWith("{")) {
      try {
        const parsed = JSON.parse(raw);
        return Object.entries(parsed).map(([code, rank]) => ({ code, count: rank }));
      } catch (e) {}
    }
    return [];
  }, [topRegions, summary]);

  return (
    <div className={`rounded-3xl border p-5 sm:p-6 shadow-2xl transition-colors ${
      isLight ? "bg-white border-zinc-200 text-zinc-900 shadow-lg" : "bg-zinc-950/80 border-white/10 backdrop-blur-xl text-white"
    }`}>
      <div className={`flex flex-wrap items-center justify-between gap-4 border-b pb-4 mb-6 ${
        isLight ? "border-zinc-200" : "border-white/10"
      }`}>
        <div>
          <h3 className={`text-base sm:text-lg font-black flex items-center gap-2 ${isLight ? "text-zinc-900" : "text-white"}`}>
            <span>💎 Gelir Dağılımı & Global Kitle Analizi</span>
          </h3>
          <p className={`text-xs mt-0.5 ${isLight ? "text-zinc-500" : "text-zinc-400"}`}>
            Eşleştirmeler (PK), Yurt Dışı kazançları, Çoklu Konuk ve Hayran Kulübü'nün toplam elmasa katkısı
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
        {/* Sol Kolon: Gelir Kanalları Progress ve Donut */}
        <div className="lg:col-span-7 space-y-4">
          <div className="text-xs font-bold uppercase tracking-wider text-amber-500 flex items-center gap-2">
            <span>📊 GELİR KANALLARI KIRILIMI</span>
          </div>

          <div className="space-y-3">
            {channels.map((ch) => (
              <div
                key={ch.id || ch.label}
                className={`rounded-2xl border p-3.5 transition-all hover:scale-[1.01] ${
                  isLight ? "bg-zinc-50 border-zinc-200" : "bg-white/[0.03] border-white/10"
                }`}
              >
                <div className="flex items-center justify-between gap-2 mb-2">
                  <div className="flex items-center gap-2">
                    <span className="h-3 w-3 rounded-full shadow-sm" style={{ backgroundColor: ch.color }} />
                    <span className="text-xs font-bold text-zinc-200" style={{ color: isLight ? "#18181b" : "#f4f4f5" }}>
                      {ch.label}
                    </span>
                    {ch.badge && (
                      <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-md border ${
                        isLight ? "bg-zinc-200/70 border-zinc-300 text-zinc-700" : "bg-white/5 border-white/10 text-zinc-400"
                      }`}>
                        {ch.badge}
                      </span>
                    )}
                  </div>

                  <div className="flex items-center gap-2 font-mono">
                    <span className="text-xs font-bold text-zinc-400">
                      {formatMetricNumber(ch.diamonds)} 💎
                    </span>
                    <span className="text-xs font-black" style={{ color: ch.color }}>
                      %{ch.pct}
                    </span>
                  </div>
                </div>

                {/* İlerleme Çubuğu */}
                <div className={`h-2.5 w-full rounded-full overflow-hidden ${isLight ? "bg-zinc-200" : "bg-zinc-800"}`}>
                  <div
                    className="h-full rounded-full transition-all duration-700 ease-out"
                    style={{
                      width: `${Math.min(100, Math.max(0, ch.pct))}%`,
                      backgroundColor: ch.color
                    }}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Sağ Kolon: Donut & Global Ülke Dağılımı + Hayran Sadakati */}
        <div className="lg:col-span-5 space-y-4">
          <div className={`rounded-2xl border p-5 ${
            isLight ? "bg-zinc-50 border-zinc-200" : "border-white/10 bg-white/[0.02]"
          }`}>
            <div className="text-xs font-bold uppercase tracking-wider text-cyan-500 mb-4 flex items-center justify-between">
              <span>🍩 GELİR KANALLARI DAĞILIMI</span>
              <span className={`text-[11px] font-mono ${isLight ? "text-zinc-500" : "text-zinc-400"}`}>
                {formatMetricNumber(totalDiamonds)} 💎
              </span>
            </div>

            <div className="flex flex-col sm:flex-row items-center gap-6 justify-center mb-6">
              <div className="relative h-36 w-36 shrink-0">
                <svg viewBox="0 0 100 100" className="h-full w-full -rotate-90">
                  <circle cx="50" cy="50" r="38" fill="none" stroke={isLight ? "#e2e8f0" : "#27272a"} strokeWidth="16" />
                  {donutSlices.map((slice, sIdx) => (
                    <circle
                      key={sIdx}
                      cx="50"
                      cy="50"
                      r="38"
                      fill="none"
                      stroke={slice.color}
                      strokeWidth="16"
                      strokeDasharray={slice.strokeDasharray}
                      strokeDashoffset={slice.strokeDashoffset}
                      className="transition-all duration-700 ease-out"
                    />
                  ))}
                </svg>
                <div className="absolute inset-0 flex flex-col items-center justify-center text-center pointer-events-none">
                  <span className={`text-[9px] font-bold uppercase tracking-wider ${isLight ? "text-zinc-500" : "text-zinc-400"}`}>
                    Lider Kanal
                  </span>
                  <span className={`text-sm font-black font-mono ${isLight ? "text-zinc-900" : "text-white"}`}>
                    %{dominantChannel?.pct || 0}
                  </span>
                </div>
              </div>

              <div className="space-y-1.5 text-xs w-full">
                {channels.map((ch, idx) => (
                  <div key={idx} className="flex items-center justify-between gap-2 font-mono">
                    <div className="flex items-center gap-1.5 truncate">
                      <span className="h-2 w-2 rounded-full shrink-0" style={{ backgroundColor: ch.color }} />
                      <span className={`truncate text-[11px] ${isLight ? "text-zinc-700 font-semibold" : "text-zinc-300"}`}>
                        {ch.label.split(" ")[1] || ch.label}
                      </span>
                    </div>
                    <span className={`font-bold text-[11px] shrink-0 ${isLight ? "text-zinc-900" : "text-white"}`}>
                      %{ch.pct}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            {/* Global Ülke Katkıları */}
            <div className={`border-t pt-4 ${isLight ? "border-zinc-200" : "border-white/10"}`}>
              <div className="text-[11px] font-bold uppercase tracking-wider text-amber-500 mb-2 flex items-center justify-between">
                <span>🌍 YURT DIŞI EN ÇOK KATKI VERENLER</span>
                <span className={`font-mono text-[10px] ${isLight ? "text-zinc-500" : "text-zinc-400"}`}>
                  {regionsList.length > 0 ? `${regionsList.length} Bölge` : "Kayıtlı Bölge Yok"}
                </span>
              </div>

              {regionsList.length > 0 ? (
                <div className="flex flex-wrap gap-2">
                  {regionsList.map((r, rIdx) => {
                    const info = COUNTRY_NAMES[r.code] || { name: r.code, flag: "🌐" };
                    return (
                      <span
                        key={rIdx}
                        className={`inline-flex items-center gap-1.5 rounded-xl px-2.5 py-1 text-xs font-bold border font-mono ${
                          isLight ? "bg-white border-zinc-200 text-zinc-800 shadow-sm" : "bg-white/5 border-white/10 text-zinc-300"
                        }`}
                        title={info.name}
                      >
                        <span>{info.flag}</span>
                        <span>{info.name}</span>
                        {r.count && <span className="text-[10px] text-amber-400 font-normal">({r.count})</span>}
                      </span>
                    );
                  })}
                </div>
              ) : (
                <p className={`text-xs ${isLight ? "text-zinc-500" : "text-zinc-500"}`}>
                  Bu dönemde yurt dışı katkı bölgesi verisi bulunmuyor.
                </p>
              )}
            </div>

            {/* Hayran Sadakati İstatistikleri */}
            <div className={`border-t pt-4 mt-4 grid grid-cols-2 gap-3 ${isLight ? "border-zinc-200" : "border-white/10"}`}>
              <div className={`rounded-xl p-2.5 border ${isLight ? "bg-white border-zinc-200" : "bg-black/40 border-white/5"}`}>
                <span className={`block text-[10px] uppercase font-bold ${isLight ? "text-zinc-500" : "text-zinc-400"}`}>
                  Toplam Hayran
                </span>
                <span className={`text-sm font-black font-mono ${isLight ? "text-zinc-900" : "text-white"}`}>
                  {formatMetricNumber(summary.totalFans || 0)}
                </span>
              </div>

              <div className={`rounded-xl p-2.5 border ${isLight ? "bg-white border-zinc-200" : "bg-black/40 border-white/5"}`}>
                <span className={`block text-[10px] uppercase font-bold ${isLight ? "text-zinc-500" : "text-zinc-400"}`}>
                  Yeni Hayranlar
                </span>
                <span className={`text-sm font-black font-mono text-emerald-400`}>
                  +{formatMetricNumber(summary.newFans || 0)}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// Geriye dönük uyumluluk için alias
export const BackstageFunnelAnalysis = BackstageRevenueAndRegions;

// 📈 3. GÜNLÜK TRENDLER & ÇOKLU METRİK GRAFİĞİ
export function BackstageMultiMetricTrend({ daily = [] }) {
  const theme = useDetectTheme();
  const isLight = theme === "light";

  const [selectedMetrics, setSelectedMetrics] = useState(["diamonds", "match_diamonds"]);
  const [hoverIndex, setHoverIndex] = useState(null);

  // Gerçek 28 Sütunlu TikTok Rapor Metrikleri
  const availableMetrics = [
    { id: "diamonds", label: "Toplam Elmas", group: "Gelir", color: "#00f2fe", unit: "💎" },
    { id: "match_diamonds", label: "PK & Maç Kazancı", group: "Gelir", color: "#eab308", unit: "💎" },
    { id: "foreign_diamonds", label: "Yurt Dışı Geliri", group: "Gelir", color: "#06b6d4", unit: "💎" },
    { id: "multi_guest_diamonds", label: "Çoklu Konuk Geliri", group: "Gelir", color: "#10b981", unit: "💎" },
    { id: "fan_club_diamonds", label: "Hayran Kulübü Geliri", group: "Gelir", color: "#ec4899", unit: "💎" },
    { id: "liveSeconds", label: "CANLI Yayın Süresi", group: "Aktivite & Kitle", color: "#3b82f6", unit: "⏱️" },
    { id: "matches", label: "PK Maç Sayısı", group: "Aktivite & Kitle", color: "#f97316", unit: "⚔️" },
    { id: "validDays", label: "Geçerli Yayın Günü", group: "Aktivite & Kitle", color: "#84cc16", unit: "📅" },
    { id: "newFollowers", label: "Yeni Takipçiler", group: "Aktivite & Kitle", color: "#a855f7", unit: "👥" },
    { id: "newFans", label: "Yeni Hayranlar", group: "Aktivite & Kitle", color: "#f43f5e", unit: "⭐" }
  ];

  const handleToggleMetric = (id) => {
    if (selectedMetrics.includes(id)) {
      if (selectedMetrics.length === 1) return;
      setSelectedMetrics(selectedMetrics.filter(m => m !== id));
    } else {
      if (selectedMetrics.length >= 5) return;
      setSelectedMetrics([...selectedMetrics, id]);
    }
  };

  const chartData = useMemo(() => {
    if (!daily || daily.length === 0) return [];

    return daily.map(d => ({
      date: d.dateTr || d.dateIso || d.date,
      diamonds: Number(d.diamonds || 0),
      match_diamonds: Number(d.match_diamonds || d.matchDiamonds || 0),
      foreign_diamonds: Number(d.foreign_diamonds || d.foreignDiamonds || 0),
      multi_guest_diamonds: Number(d.multi_guest_diamonds || d.multiGuestDiamonds || 0),
      fan_club_diamonds: Number(d.fan_club_diamonds || d.fanClubDiamonds || 0),
      liveSeconds: Number(d.liveSeconds || d.live_seconds || 0),
      matches: Number(d.matches || 0),
      validDays: Number(d.validDays || d.valid_days || 0),
      newFollowers: Number(d.newFollowers || d.new_followers || 0),
      newFans: Number(d.new_fans || d.newFans || 0)
    }));
  }, [daily]);

  const chartW = 850;
  const chartH = 320;
  const pad = { top: 30, right: 30, bottom: 45, left: 65 };
  const innerW = chartW - pad.left - pad.right;
  const innerH = chartH - pad.top - pad.bottom;

  const maxVal = useMemo(() => {
    let m = 100;
    chartData.forEach(d => {
      selectedMetrics.forEach(metricId => {
        const v = Number(d[metricId] || 0);
        if (v > m) m = v;
      });
    });
    return m;
  }, [chartData, selectedMetrics]);

  const linesMap = useMemo(() => {
    const result = {};
    const n = chartData.length;
    if (n < 2) return result;

    selectedMetrics.forEach(metricId => {
      const points = chartData.map((d, idx) => {
        const x = pad.left + (idx / (n - 1)) * innerW;
        const v = Number(d[metricId] || 0);
        const y = pad.top + innerH - (maxVal > 0 ? (v / maxVal) * innerH : 0);
        return { x, y, val: v };
      });

      const polylineStr = points.map(p => `${p.x},${p.y}`).join(" ");
      const areaStr = `${pad.left},${pad.top + innerH} ${polylineStr} ${pad.left + innerW},${pad.top + innerH}`;

      result[metricId] = { points, polylineStr, areaStr };
    });

    return result;
  }, [chartData, selectedMetrics, maxVal, innerW, innerH, pad]);

  const activeHoverData = hoverIndex !== null && chartData[hoverIndex] ? chartData[hoverIndex] : null;

  const gridStroke = isLight ? "rgba(0, 0, 0, 0.12)" : "rgba(255, 255, 255, 0.08)";
  const yAxisFill = isLight ? "#1e293b" : "rgba(255, 255, 255, 0.75)";
  const xAxisFill = isLight ? "#0f172a" : "rgba(255, 255, 255, 0.85)";

  return (
    <div className={`rounded-3xl border p-5 sm:p-6 shadow-2xl transition-colors ${
      isLight ? "bg-white border-zinc-200 text-zinc-900 shadow-lg" : "bg-zinc-950/80 border-white/10 backdrop-blur-xl text-white"
    }`}>
      <div className={`flex flex-wrap items-center justify-between gap-4 border-b pb-4 mb-6 ${
        isLight ? "border-zinc-200" : "border-white/10"
      }`}>
        <div>
          <h3 className={`text-base sm:text-lg font-black flex items-center gap-2 ${isLight ? "text-zinc-900" : "text-white"}`}>
            <span>📈 Günlük Trendler & Çoklu Metrik Analizi</span>
          </h3>
          <p className={`text-xs mt-0.5 ${isLight ? "text-zinc-500" : "text-zinc-400"}`}>
            Dönem içindeki değişimleri incelemek için sağdaki listeden en fazla 5 metrik seçebilirsiniz.
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          {selectedMetrics.map(mId => {
            const mObj = availableMetrics.find(x => x.id === mId);
            if (!mObj) return null;
            return (
              <span
                key={mId}
                className="inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-bold font-mono border shadow-sm"
                style={{
                  backgroundColor: `${mObj.color}18`,
                  borderColor: `${mObj.color}50`,
                  color: mObj.color
                }}
              >
                <span className="h-2 w-2 rounded-full shadow-sm" style={{ backgroundColor: mObj.color }} />
                <span>{mObj.label}</span>
              </span>
            );
          })}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-start">
        <div className="lg:col-span-8 relative">
          <div className="w-full overflow-x-auto">
            <svg
              viewBox={`0 0 ${chartW} ${chartH}`}
              className="w-full h-auto min-w-[550px] overflow-visible select-none"
              onMouseLeave={() => setHoverIndex(null)}
            >
              <defs>
                {availableMetrics.map(m => (
                  <linearGradient key={m.id} id={`grad-${m.id}`} x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={m.color} stopOpacity="0.45" />
                    <stop offset="100%" stopColor={m.color} stopOpacity="0.0" />
                  </linearGradient>
                ))}
              </defs>

              {/* Y Ekseni Izgara Çizgileri ve Metinler */}
              {[1, 0.75, 0.5, 0.25, 0].map((step, sIdx) => {
                const y = pad.top + innerH * (1 - step);
                const valLabel = formatMetricNumber(Math.round(maxVal * step));
                return (
                  <g key={sIdx}>
                    <line
                      x1={pad.left}
                      x2={pad.left + innerW}
                      y1={y}
                      y2={y}
                      stroke={gridStroke}
                      strokeDasharray="4 4"
                    />
                    <text
                      x={pad.left - 10}
                      y={y + 4}
                      fill={yAxisFill}
                      fontSize="10"
                      fontWeight={isLight ? "700" : "500"}
                      textAnchor="end"
                      fontFamily="monospace"
                    >
                      {valLabel}
                    </text>
                  </g>
                );
              })}

              {/* Hover Çizgisi */}
              {chartData.length > 1 && hoverIndex !== null && (
                <line
                  x1={pad.left + (hoverIndex / (chartData.length - 1)) * innerW}
                  x2={pad.left + (hoverIndex / (chartData.length - 1)) * innerW}
                  y1={pad.top}
                  y2={pad.top + innerH}
                  stroke="#f59e0b"
                  strokeWidth="2"
                  strokeDasharray="3 3"
                />
              )}

              {/* Metrik Çizgileri ve Alanları */}
              {chartData.length > 1 && selectedMetrics.map(mId => {
                const lineInfo = linesMap[mId];
                if (!lineInfo) return null;
                const mObj = availableMetrics.find(x => x.id === mId);
                const color = mObj ? mObj.color : "#00f2fe";

                return (
                  <g key={mId}>
                    <polygon
                      points={lineInfo.areaStr}
                      fill={`url(#grad-${mId})`}
                    />
                    <polyline
                      points={lineInfo.polylineStr}
                      fill="none"
                      stroke={color}
                      strokeWidth="3"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    {lineInfo.points.map((pt, pIdx) => (
                      <circle
                        key={pIdx}
                        cx={pt.x}
                        cy={pt.y}
                        r={hoverIndex === pIdx ? "6" : "3"}
                        fill={color}
                        stroke={isLight ? "#ffffff" : "#09090b"}
                        strokeWidth="2"
                        className="transition-all"
                      />
                    ))}
                  </g>
                );
              })}

              {/* X Ekseni Tarih Etiketleri */}
              {chartData.length > 1 && chartData.map((d, idx) => {
                const step = chartData.length > 15 ? 3 : chartData.length > 8 ? 2 : 1;
                if (idx % step !== 0 && idx !== chartData.length - 1 && idx !== 0) return null;
                const x = pad.left + (idx / (chartData.length - 1)) * innerW;
                const label = typeof d.date === "string" && d.date.length > 10 ? d.date.slice(0, 10) : d.date;
                return (
                  <text
                    key={idx}
                    x={x}
                    y={chartH - 12}
                    fill={xAxisFill}
                    fontSize="10"
                    fontWeight={isLight ? "700" : "500"}
                    textAnchor="middle"
                    fontFamily="monospace"
                  >
                    {label}
                  </text>
                );
              })}

              {/* Hover Alanları */}
              {chartData.length > 1 && chartData.map((d, idx) => {
                const colW = innerW / chartData.length;
                const x = pad.left + (idx / (chartData.length - 1)) * innerW - colW / 2;
                return (
                  <rect
                    key={idx}
                    x={x}
                    y={pad.top}
                    width={colW}
                    height={innerH}
                    fill="transparent"
                    className="cursor-crosshair"
                    onMouseEnter={() => setHoverIndex(idx)}
                  />
                );
              })}
            </svg>
          </div>

          {/* Hover Bilgi Kartı (Tooltip) */}
          {activeHoverData && hoverIndex !== null && (
            <div
              className={`absolute pointer-events-none rounded-2xl border p-3.5 backdrop-blur-xl shadow-2xl transition-all z-20 ${
                isLight ? "bg-white/95 border-zinc-300 text-zinc-900" : "bg-black/90 border-white/20 text-white"
              }`}
              style={{
                left: Math.min(Math.max(pad.left + (hoverIndex / (chartData.length - 1)) * innerW - 100, 10), chartW - 220),
                top: 10
              }}
            >
              <div className={`text-xs font-bold font-mono border-b pb-1 mb-2 ${isLight ? "text-amber-600 border-zinc-200" : "text-amber-400 border-white/10"}`}>
                📅 {activeHoverData.date}
              </div>
              <div className="space-y-1.5 text-xs">
                {selectedMetrics.map(mId => {
                  const mObj = availableMetrics.find(x => x.id === mId);
                  if (!mObj) return null;
                  const v = activeHoverData[mId] || 0;
                  const formattedVal = mId === "liveSeconds" ? formatLiveDuration(v) : formatMetricNumber(v);

                  return (
                    <div key={mId} className="flex items-center justify-between gap-4 font-mono">
                      <span className="flex items-center gap-1.5" style={{ color: mObj.color }}>
                        <span className="h-2 w-2 rounded-full" style={{ backgroundColor: mObj.color }} />
                        <span className="text-[11px] font-semibold">{mObj.label}:</span>
                      </span>
                      <span className={`font-black ${isLight ? "text-zinc-900" : "text-white"}`}>
                        {formattedVal} {mId !== "liveSeconds" ? mObj.unit : ""}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        {/* Metrik Seçim Paneli */}
        <div className={`lg:col-span-4 rounded-2xl border p-4 ${
          isLight ? "bg-zinc-50 border-zinc-200" : "border-white/10 bg-white/[0.02]"
        }`}>
          <div className={`text-xs font-bold uppercase tracking-wider text-amber-500 flex items-center justify-between mb-3 border-b pb-2 ${
            isLight ? "border-zinc-200" : "border-white/10"
          }`}>
            <span>🎛️ EN FAZLA 5 METRİK SEÇİNİZ</span>
            <span className={`font-mono ${isLight ? "text-zinc-600" : "text-zinc-400"}`}>{selectedMetrics.length} / 5</span>
          </div>

          <div className="space-y-2 mb-4">
            <div className="text-[11px] font-bold text-cyan-500 uppercase tracking-wider">▼ GELİR METRİKLERİ</div>
            {availableMetrics.filter(m => m.group === "Gelir").map(m => {
              const isChecked = selectedMetrics.includes(m.id);
              return (
                <label
                  key={m.id}
                  className={`flex items-center gap-2.5 rounded-xl p-2 text-xs font-medium cursor-pointer transition ${
                    isChecked
                      ? "bg-cyan-500/15 text-cyan-600 dark:text-white border border-cyan-500/40 font-bold shadow-sm"
                      : isLight ? "text-zinc-700 hover:bg-zinc-200/60" : "text-zinc-400 hover:bg-white/5"
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={isChecked}
                    onChange={() => handleToggleMetric(m.id)}
                    className="h-4 w-4 rounded border-zinc-400 dark:border-zinc-700 bg-white dark:bg-zinc-900 text-cyan-500 focus:ring-0 cursor-pointer"
                  />
                  <span>{m.label}</span>
                </label>
              );
            })}
          </div>

          <div className="space-y-2">
            <div className="text-[11px] font-bold text-amber-500 uppercase tracking-wider">▼ AKTİVİTE & KİTLE</div>
            {availableMetrics.filter(m => m.group === "Aktivite & Kitle").map(m => {
              const isChecked = selectedMetrics.includes(m.id);
              return (
                <label
                  key={m.id}
                  className={`flex items-center gap-2.5 rounded-xl p-2 text-xs font-medium cursor-pointer transition ${
                    isChecked
                      ? "bg-amber-500/15 text-amber-600 dark:text-white border border-amber-500/40 font-bold shadow-sm"
                      : isLight ? "text-zinc-700 hover:bg-zinc-200/60" : "text-zinc-400 hover:bg-white/5"
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={isChecked}
                    onChange={() => handleToggleMetric(m.id)}
                    className="h-4 w-4 rounded border-zinc-400 dark:border-zinc-700 bg-white dark:bg-zinc-900 text-amber-500 focus:ring-0 cursor-pointer"
                  />
                  <span>{m.label}</span>
                </label>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}

// 📑 4. GÜNLÜK DETAYLAR TABLOSU & GERÇEK 28 SÜTUN SEÇİMİ
export function BackstageDailyDetailsTable({ daily = [] }) {
  const [columns, setColumns] = useState({
    diamonds: true,
    match_diamonds: true,
    matches: false,
    foreign_diamonds: true,
    multi_guest_diamonds: true,
    fan_club_diamonds: false,
    liveSeconds: true,
    validDays: false,
    newFollowers: false
  });
  const [customizing, setCustomizing] = useState(false);
  const [showAll, setShowAll] = useState(false);

  const columnLabels = {
    diamonds: "Elmaslar",
    match_diamonds: "PK Kazancı",
    matches: "PK Maç Sayısı",
    foreign_diamonds: "Yurt Dışı Elmas",
    multi_guest_diamonds: "Çoklu Konuk",
    fan_club_diamonds: "Hayran Kulübü",
    liveSeconds: "Yayın Süresi",
    validDays: "Geçerli Gün",
    newFollowers: "Yeni Takipçi"
  };

  const toggleCol = (key) => setColumns({ ...columns, [key]: !columns[key] });

  const sortedDaily = useMemo(() => {
    if (!daily || !Array.isArray(daily)) return [];
    return [...daily].reverse();
  }, [daily]);

  const displayedDaily = showAll ? sortedDaily : sortedDaily.slice(0, 15);

  return (
    <div className="rounded-3xl border border-white/10 bg-zinc-950/80 backdrop-blur-xl p-5 sm:p-6 shadow-2xl">
      <div className="flex flex-wrap items-center justify-between gap-4 border-b border-white/10 pb-4 mb-4">
        <div>
          <h3 className="text-base sm:text-lg font-black text-white flex items-center gap-2">
            <span>📋 Günlük Performans Detayları</span>
          </h3>
          <p className="text-xs text-zinc-400 mt-0.5">
            Seçili döneme ait tarih bazlı elmas, PK, yurt dışı ve yayın süresi dökümü ({sortedDaily.length} gün)
          </p>
        </div>

        <div className="flex items-center gap-2">
          {sortedDaily.length > 15 && (
            <button
              type="button"
              onClick={() => setShowAll(!showAll)}
              className="rounded-xl border border-white/10 bg-white/5 px-3 py-2 text-xs font-bold text-zinc-300 hover:bg-white/10 transition cursor-pointer"
            >
              {showAll ? "İlk 15 Günü Göster" : `Tümünü Göster (${sortedDaily.length} Gün)`}
            </button>
          )}

          <button
            type="button"
            onClick={() => setCustomizing(!customizing)}
            className="inline-flex items-center gap-2 rounded-xl border border-cyan-500/40 bg-cyan-500/10 px-3.5 py-2 text-xs font-bold text-cyan-300 hover:bg-cyan-500/20 transition cursor-pointer"
          >
            <span>⚙️</span>
            <span>Verileri Özelleştirin</span>
          </button>
        </div>
      </div>

      {customizing && (
        <div className="mb-4 rounded-2xl border border-white/10 bg-black/60 p-3.5 flex flex-wrap items-center gap-4 text-xs">
          <span className="font-bold text-amber-400">Görüntülenecek Sütunlar:</span>
          {Object.entries(columns).map(([k, v]) => (
            <label key={k} className="flex items-center gap-1.5 cursor-pointer text-zinc-300">
              <input
                type="checkbox"
                checked={v}
                onChange={() => toggleCol(k)}
                className="rounded border-zinc-700 bg-zinc-900 text-amber-500"
              />
              <span>{columnLabels[k] || k}</span>
            </label>
          ))}
        </div>
      )}

      <div className="overflow-x-auto rounded-2xl border border-white/10">
        <table className="w-full text-left text-xs text-zinc-300">
          <thead className="bg-zinc-900/90 text-[11px] uppercase tracking-wider font-bold text-zinc-400 border-b border-white/10 font-mono">
            <tr>
              <th className="p-3.5">Tarih (UTC+00:00)</th>
              {columns.diamonds && <th className="p-3.5 text-right text-cyan-400">Elmaslar</th>}
              {columns.match_diamonds && <th className="p-3.5 text-right text-amber-400">PK Kazancı</th>}
              {columns.matches && <th className="p-3.5 text-right">PK Maçı</th>}
              {columns.foreign_diamonds && <th className="p-3.5 text-right text-cyan-300">Yurt Dışı</th>}
              {columns.multi_guest_diamonds && <th className="p-3.5 text-right text-emerald-400">Çoklu Konuk</th>}
              {columns.fan_club_diamonds && <th className="p-3.5 text-right text-pink-400">Hayran Kulübü</th>}
              {columns.liveSeconds && <th className="p-3.5 text-right">Yayın Süresi</th>}
              {columns.validDays && <th className="p-3.5 text-right">Geçerli Gün</th>}
              {columns.newFollowers && <th className="p-3.5 text-right">Yeni Takipçi</th>}
            </tr>
          </thead>
          <tbody className="divide-y divide-white/5 font-mono">
            {displayedDaily && displayedDaily.length > 0 ? (
              displayedDaily.map((row, idx) => {
                const dia = Number(row.diamonds || 0);
                const matchDia = Number(row.match_diamonds || row.matchDiamonds || 0);
                const matchesCnt = Number(row.matches || 0);
                const foreignDia = Number(row.foreign_diamonds || row.foreignDiamonds || 0);
                const guestDia = Number(row.multi_guest_diamonds || row.multiGuestDiamonds || 0);
                const fanDia = Number(row.fan_club_diamonds || row.fanClubDiamonds || 0);
                const sec = Number(row.liveSeconds || row.live_seconds || 0);
                const vDays = Number(row.validDays || row.valid_days || (sec >= 3600 ? 1 : 0));
                const fol = Number(row.newFollowers || row.new_followers || 0);

                return (
                  <tr key={idx} className="hover:bg-white/[0.04] transition">
                    <td className="p-3.5 font-bold text-white">{row.dateTr || row.dateIso || row.date}</td>
                    {columns.diamonds && (
                      <td className="p-3.5 text-right font-bold text-cyan-300">
                        {dia.toLocaleString("tr-TR")}
                      </td>
                    )}
                    {columns.match_diamonds && (
                      <td className="p-3.5 text-right font-bold text-amber-300">
                        {matchDia.toLocaleString("tr-TR")}
                      </td>
                    )}
                    {columns.matches && (
                      <td className="p-3.5 text-right text-zinc-400">
                        {matchesCnt}
                      </td>
                    )}
                    {columns.foreign_diamonds && (
                      <td className="p-3.5 text-right text-cyan-400">
                        {foreignDia.toLocaleString("tr-TR")}
                      </td>
                    )}
                    {columns.multi_guest_diamonds && (
                      <td className="p-3.5 text-right text-emerald-300">
                        {guestDia.toLocaleString("tr-TR")}
                      </td>
                    )}
                    {columns.fan_club_diamonds && (
                      <td className="p-3.5 text-right text-pink-300">
                        {fanDia.toLocaleString("tr-TR")}
                      </td>
                    )}
                    {columns.liveSeconds && (
                      <td className="p-3.5 text-right text-zinc-300">
                        {formatLiveDuration(sec)}
                      </td>
                    )}
                    {columns.validDays && (
                      <td className="p-3.5 text-right text-zinc-400">
                        {vDays}
                      </td>
                    )}
                    {columns.newFollowers && (
                      <td className="p-3.5 text-right text-zinc-400">
                        +{fol.toLocaleString("tr-TR")}
                      </td>
                    )}
                  </tr>
                );
              })
            ) : (
              <tr>
                <td colSpan="10" className="p-6 text-center text-zinc-500 font-sans">
                  Seçili tarih aralığında kayıtlı günlük veri bulunamadı.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// 💬 5. INSTANT MESSAGES YÜZEN BİLDİRİM TOAST
export function InstantMessagesWidget({
  currentPublisher = {},
  unreadCount = 2,
  onOpenChat = () => {}
}) {
  const [isOpen, setIsOpen] = useState(true);

  if (!isOpen) return null;

  return (
    <div className="fixed bottom-6 right-6 z-50 animate-bounce-short">
      <div className="relative overflow-hidden rounded-2xl border border-amber-500/50 bg-gradient-to-r from-zinc-950 via-zinc-900 to-black p-4 shadow-[0_15px_40px_rgba(0,0,0,0.8)] text-white max-w-sm">
        <button
          type="button"
          onClick={() => setIsOpen(false)}
          className="absolute top-2.5 right-2.5 h-6 w-6 rounded-full bg-white/10 text-xs text-zinc-400 hover:text-white flex items-center justify-center transition cursor-pointer"
        >
          ✕
        </button>

        <div className="flex items-start gap-3.5 pr-6">
          <div className="relative flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-amber-500/40 bg-amber-500/15 text-lg shadow-inner">
            🔔
            {unreadCount > 0 && (
              <span className="absolute -top-1 -right-1 flex h-4 w-4 items-center justify-center rounded-full bg-rose-500 text-[10px] font-black text-white">
                {unreadCount}
              </span>
            )}
          </div>

          <div>
            <div className="text-xs font-black text-white flex items-center gap-1.5">
              <span>Yeni Mesajlarınız Var</span>
              <span className="h-1.5 w-1.5 rounded-full bg-amber-400 animate-ping" />
            </div>
            <p className="text-[11px] text-zinc-300 mt-1 leading-snug">
              Temsilcinizden yeni bir PK hedefi ve sistem güncellemesi iletildi.
            </p>

            <button
              type="button"
              onClick={onOpenChat}
              className="mt-2.5 inline-flex items-center gap-1.5 rounded-lg bg-amber-500 px-3 py-1.5 text-[11px] font-black text-black hover:bg-amber-400 transition shadow cursor-pointer"
            >
              <span>Mesajları Gör</span>
              <span>›</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// 👑 TÜM SUITE'İ VE DİNAMİK TARİH/AY HESAPLAMA MOTORUNU YÖNETEN ANA COMPONENT
export default function TikTokAnalyticsSuite({
  performanceData = {},
  previousMonthPerformance = {},
  currentPublisher = {},
  onOpenMessages = () => {},
  onDateRangeChange = null
}) {
  const realNow = useMemo(() => new Date(), []);
  
  const [period, setPeriod] = useState("month"); // 'month', 'week', 'yesterday', 'specific_month', 'custom'
  const [selectedYear, setSelectedYear] = useState(realNow.getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(realNow.getMonth());

  // Özel tarih seçimi state'leri (Varsayılan olarak bu ayın 1'i ve bugünün tarihi)
  const [customStart, setCustomStart] = useState(() => {
    const d = new Date(realNow.getFullYear(), realNow.getMonth(), 1);
    return formatDateIso(d);
  });
  const [customEnd, setCustomEnd] = useState(() => {
    return formatDateIso(realNow);
  });

  // Ay seçildiğinde çalışacak handler
  const handleMonthSelect = (year, month) => {
    setSelectedYear(year);
    setSelectedMonth(month);
    setPeriod("specific_month");
  };

  // Seçili döneme göre aktif ve önceki tarih aralıklarını hesapla
  const dateRanges = useMemo(() => {
    const now = new Date();
    const currYear = now.getFullYear();
    const currMonth = now.getMonth();
    const currDay = now.getDate();

    let activeStart = new Date(currYear, currMonth, 1);
    let activeEnd = new Date(currYear, currMonth, currDay);

    let prevStart = new Date(currYear, currMonth - 1, 1);
    let prevEnd = new Date(currYear, currMonth - 1, currDay);

    if (period === "month") {
      activeStart = new Date(currYear, currMonth, 1);
      activeEnd = new Date(currYear, currMonth, currDay);

      const prevMonthLastDay = new Date(currYear, currMonth, 0).getDate();
      const safePrevDay = Math.min(currDay, prevMonthLastDay);
      prevStart = new Date(currYear, currMonth - 1, 1);
      prevEnd = new Date(currYear, currMonth - 1, safePrevDay);
    } else if (period === "week") {
      activeEnd = new Date(currYear, currMonth, currDay);
      activeStart = new Date(currYear, currMonth, currDay - 6);

      prevEnd = new Date(currYear, currMonth, currDay - 7);
      prevStart = new Date(currYear, currMonth, currDay - 13);
    } else if (period === "yesterday") {
      activeStart = new Date(currYear, currMonth, currDay - 1);
      activeEnd = new Date(currYear, currMonth, currDay - 1);

      prevStart = new Date(currYear, currMonth, currDay - 2);
      prevEnd = new Date(currYear, currMonth, currDay - 2);
    } else if (period === "specific_month") {
      const lastDayOfSelectedMonth = new Date(selectedYear, selectedMonth + 1, 0).getDate();
      const isCurrentCalendarMonth = selectedYear === currYear && selectedMonth === currMonth;
      const targetEndDay = isCurrentCalendarMonth ? currDay : lastDayOfSelectedMonth;

      activeStart = new Date(selectedYear, selectedMonth, 1);
      activeEnd = new Date(selectedYear, selectedMonth, targetEndDay);

      const lastDayOfPrevMonth = new Date(selectedYear, selectedMonth, 0).getDate();
      const prevEndDay = isCurrentCalendarMonth ? Math.min(currDay, lastDayOfPrevMonth) : lastDayOfPrevMonth;
      prevStart = new Date(selectedYear, selectedMonth - 1, 1);
      prevEnd = new Date(selectedYear, selectedMonth - 1, prevEndDay);
    } else if (period === "custom") {
      activeStart = parseDateStr(customStart);
      activeEnd = parseDateStr(customEnd);

      const diffMs = activeEnd.getTime() - activeStart.getTime();
      const diffDays = Math.max(1, Math.round(diffMs / (1000 * 60 * 60 * 24)) + 1);

      prevEnd = new Date(activeStart.getTime() - 24 * 60 * 60 * 1000);
      prevStart = new Date(prevEnd.getTime() - (diffDays - 1) * 24 * 60 * 60 * 1000);
    }

    return {
      activeStart,
      activeEnd,
      prevStart,
      prevEnd,
      activeLabel: `${formatDateTr(activeStart)} - ${formatDateTr(activeEnd)}`,
      prevLabel: `${formatDateTr(prevStart)} - ${formatDateTr(prevEnd)}`
    };
  }, [period, selectedYear, selectedMonth, customStart, customEnd]);

  // 🔄 CANLI VE GERÇEK VERİTABANI (PLESK MYSQL) VERİ MOTORU
  const [apiData, setApiData] = useState(null);
  const [prevApiData, setPrevApiData] = useState(null);
  const [apiLoading, setApiLoading] = useState(false);

  const hiddenId = currentPublisher?.hiddenId || performanceData?.hiddenId || "";
  const username = currentPublisher?.username || "";

  useEffect(() => {
    let ignore = false;

    async function loadRealApiData() {
      if (!hiddenId && !username) return;

      try {
        setApiLoading(true);
        const startIso = formatDateIso(dateRanges.activeStart);
        const endIso = formatDateIso(dateRanges.activeEnd);
        const prevStartIso = formatDateIso(dateRanges.prevStart);
        const prevEndIso = formatDateIso(dateRanges.prevEnd);

        const userId = currentPublisher?.id || "";
        const currUrl = `${API_PERFORMANCE_SUMMARY}?hiddenId=${encodeURIComponent(hiddenId || "")}&username=${encodeURIComponent(username || "")}&userId=${encodeURIComponent(userId || "")}&startDate=${startIso}&endDate=${endIso}`;
        const prevUrl = `${API_PERFORMANCE_SUMMARY}?hiddenId=${encodeURIComponent(hiddenId || "")}&username=${encodeURIComponent(username || "")}&userId=${encodeURIComponent(currentPublisher?.id || "")}&startDate=${prevStartIso}&endDate=${prevEndIso}`;

        const [currRes, prevRes] = await Promise.all([
          fetch(currUrl, { cache: "no-store" }),
          fetch(prevUrl, { cache: "no-store" })
        ]);

        if (!ignore) {
          if (currRes.ok) {
            const currJson = await currRes.json();
            if (currJson && currJson.success) {
              setApiData(currJson);
            }
          }
          if (prevRes.ok) {
            const prevJson = await prevRes.json();
            if (prevJson && prevJson.success) {
              setPrevApiData(prevJson);
            }
          }
        }
      } catch (err) {
        console.warn("Performance API fetch error:", err);
      } finally {
        if (!ignore) {
          setApiLoading(false);
        }
      }
    }

    loadRealApiData();

    return () => {
      ignore = true;
    };
  }, [hiddenId, username, dateRanges.activeStart, dateRanges.activeEnd, dateRanges.prevStart, dateRanges.prevEnd]);

  // Üst bileşene tarih aralığı değişikliğini bildir (varsa)
  useEffect(() => {
    if (typeof onDateRangeChange === "function") {
      onDateRangeChange(formatDateIso(dateRanges.activeStart), formatDateIso(dateRanges.activeEnd));
    }
  }, [dateRanges.activeStart, dateRanges.activeEnd, onDateRangeChange]);

  // Tarih aralıklarına göre günleri getir (Veritabanındaki gerçek verileri 1-e-1 eşleştirir)
  const filteredDaily = useMemo(() => {
    const sourceDaily = (apiData && Array.isArray(apiData.daily)) ? apiData.daily : (performanceData?.daily || []);
    return getDailyDataForRange(dateRanges.activeStart, dateRanges.activeEnd, sourceDaily);
  }, [apiData, dateRanges.activeStart, dateRanges.activeEnd, performanceData?.daily]);

  const prevFilteredDaily = useMemo(() => {
    const prevSourceDaily = (prevApiData && Array.isArray(prevApiData.daily)) 
      ? prevApiData.daily 
      : (previousMonthPerformance?.daily || performanceData?.daily || []);
    return getDailyDataForRange(dateRanges.prevStart, dateRanges.prevEnd, prevSourceDaily);
  }, [prevApiData, dateRanges.prevStart, dateRanges.prevEnd, previousMonthPerformance?.daily, performanceData?.daily]);

  // Aktif ve önceki dönem özetlerini hesapla (Plesk MySQL'den gelen kesin toplamları kullanır)
  const activeSummary = useMemo(() => {
    if (apiData && apiData.summary) {
      const s = apiData.summary;
      return {
        diamonds: Number(s.totalDiamonds || 0),
        liveDays: Number(s.activeDays || 0),
        validDays: Number(s.validDays || 0),
        liveSeconds: Number(s.totalLiveSeconds || 0),
        liveCount: Number(s.totalLiveCount || 0),
        newFollowers: Number(s.newFollowers || 0),
        matches: Number(s.matches || 0),
        matchDiamonds: Number(s.matchDiamonds || 0),
        multiGuestDiamonds: Number(s.multiGuestDiamonds || 0),
        multiHostDiamonds: Number(s.multiHostDiamonds || 0),
        multiGuestOnlyDiamonds: Number(s.multiGuestOnlyDiamonds || 0),
        foreignDiamonds: Number(s.foreignDiamonds || 0),
        fanClubDiamonds: Number(s.fanClubDiamonds || 0),
        newFans: Number(s.newFans || 0),
        totalFans: Number(s.totalFans || 0),
        activeFans: Number(s.activeFans || 0),
        tierStatus: s.tierStatus || "",
        graduationStatus: s.graduationStatus || "",
        agencyGroup: s.agencyGroup || "",
        managerEmail: s.managerEmail || ""
      };
    }

    return calculatePeriodSummary(filteredDaily);
  }, [apiData, filteredDaily]);

  const prevActiveSummary = useMemo(() => {
    if (prevApiData && prevApiData.summary) {
      const s = prevApiData.summary;
      return {
        diamonds: Number(s.totalDiamonds || 0),
        liveDays: Number(s.activeDays || 0),
        validDays: Number(s.validDays || 0),
        liveSeconds: Number(s.totalLiveSeconds || 0),
        liveCount: Number(s.totalLiveCount || 0),
        newFollowers: Number(s.newFollowers || 0),
        matches: Number(s.matches || 0),
        matchDiamonds: Number(s.matchDiamonds || 0),
        multiGuestDiamonds: Number(s.multiGuestDiamonds || 0),
        multiHostDiamonds: Number(s.multiHostDiamonds || 0),
        multiGuestOnlyDiamonds: Number(s.multiGuestOnlyDiamonds || 0),
        foreignDiamonds: Number(s.foreignDiamonds || 0),
        fanClubDiamonds: Number(s.fanClubDiamonds || 0),
        newFans: Number(s.newFans || 0),
        totalFans: Number(s.totalFans || 0),
        activeFans: Number(s.activeFans || 0),
        tierStatus: s.tierStatus || "",
        graduationStatus: s.graduationStatus || "",
        agencyGroup: s.agencyGroup || "",
        managerEmail: s.managerEmail || ""
      };
    }

    return calculatePeriodSummary(prevFilteredDaily);
  }, [prevApiData, prevFilteredDaily]);

  const handleQuickPreset = (preset) => {
    const now = new Date();
    if (preset.range) {
      setCustomStart(preset.range[0]);
      setCustomEnd(preset.range[1]);
    } else if (preset.days) {
      const endD = new Date(now.getTime());
      const startD = new Date(now.getTime() - (preset.days - 1) * 24 * 60 * 60 * 1000);
      setCustomStart(formatDateIso(startD));
      setCustomEnd(formatDateIso(endD));
    }
  };

  return (
    <div className="space-y-8">
      {/* 1. Temel Metrikler (Dönem Seçimi & Karşılaştırmalı Kartlar) */}
      <BackstageKpiCards
        summary={activeSummary}
        previousSummary={prevActiveSummary}
        dateRangeLabel={dateRanges.activeLabel}
        previousDateRangeLabel={dateRanges.prevLabel}
        activePeriod={period}
        onPeriodChange={setPeriod}
        selectedYear={selectedYear}
        selectedMonth={selectedMonth}
        onMonthSelect={handleMonthSelect}
        customStartDate={customStart}
        customEndDate={customEnd}
        onCustomStartChange={setCustomStart}
        onCustomEndChange={setCustomEnd}
        onQuickPreset={handleQuickPreset}
      />

      {/* 2. Gelir Kanalları Kırılımı & Global Kitle Analizi */}
      <BackstageRevenueAndRegions
        summary={activeSummary}
        previousSummary={prevActiveSummary}
        revenueBreakdown={apiData?.revenueBreakdown}
        topRegions={apiData?.summary?.topRegions}
      />

      {/* 3. Günlük Trendler & Çoklu Metrik Grafiği */}
      <BackstageMultiMetricTrend daily={filteredDaily} />

      {/* 4. Günlük Performans Detayları Tablosu */}
      <BackstageDailyDetailsTable daily={filteredDaily} />

      {/* 5. Instant Messages Yüzen Bildirim Kartı */}
      <InstantMessagesWidget
        currentPublisher={currentPublisher}
        unreadCount={2}
        onOpenChat={onOpenMessages}
      />
    </div>
  );
}